import { HOTEL_PRODUCT_TYPE } from 'constants/booking';

import { PACKAGE_HOTEL_MARGIN_PERCENTAGE } from 'constants/booking';
import { subtract } from 'utils/calculator';

export const buildRefundProposal = (data) => {
  const hotelRooms = data?.cancelHotelRefundProposal?.hotelRooms ?? [];
  return {
    breakdown: {
      hotel: { amount: hotelRooms.reduce((acc, room) => acc + room.penalty - room.margin, 0) },
      hotelMargin: { amount: hotelRooms.reduce((acc, room) => acc + room.margin, 0) },
      refund: { amount: hotelRooms.reduce((acc, room) => acc + room.refund, 0) }
    }
  };
};

export const buildRefundProposalFromBookingPenalty = (penaltiesApplied, hotels) => ({
  breakdown: {
    hotel: { amount: penaltiesApplied.value },
    hotelMargin: { amount: penaltiesApplied.margin },
    refund: { amount: hotels[0].priceWithMargin - penaltiesApplied.margin - penaltiesApplied.value }
  }
});

export const buildHotelInfo = (hotelBooking) => {
  const isPackageProductType = HOTEL_PRODUCT_TYPE[hotelBooking?.productType] === HOTEL_PRODUCT_TYPE.PACKAGES;

  const margin = isPackageProductType
    ? Number(((hotelBooking?.totalPrice * PACKAGE_HOTEL_MARGIN_PERCENTAGE) / 100).toFixed(2))
    : Number(hotelBooking?.totalCommission?.toFixed(2));

  const price = isPackageProductType ? hotelBooking?.totalPrice : subtract(hotelBooking?.totalPrice || 0, margin || 0);

  return {
    ...hotelBooking,
    isDotW: false,
    margin,
    price
  };
};

export function buildHotelInfoFromDotwBooking(data) {
  const isConfirmed = data?.rooms.some(({ status }) => ['AMENDED_CONFIRM', 'CONFIRMED'].includes(status));
  const isCancelled = data?.rooms.every(({ status }) => !['AMENDED_CONFIRM', 'CONFIRMED'].includes(status));

  return {
    isDotW: true,
    productType: data?.product,
    leadGuestFirstName: data?.rooms.find((r) => r.passengersDetails?.some((p) => p.leading))?.passengersDetails?.find((p) => p.leading)
      ?.firstName,
    leadGuestLastName: data?.rooms.find((r) => r.passengersDetails?.some((p) => p.leading))?.passengersDetails?.find((p) => p.leading)
      ?.lastName,
    arrivalDate: data?.rooms[0]?.from,
    bookingReference: data?.bookingCode,
    hotelId: data?.hotel.id,
    hotelName: data?.hotel.name,
    duration: data?.rooms[0]?.duration,
    numberOfGuests: Number(data?.rooms.reduce((acc, room) => acc + Number(room.adults) + room.children.length, 0)),
    numberOfRooms: data?.rooms.length,
    address: data?.hotel.address,
    rating: data?.hotel.rating,
    region: data?.hotel.region,
    resort: '-',
    country: data?.region.countryForVat,
    telephone: data?.hotel.phone,
    rooms: data?.rooms.map((room) => ({
      type: room.roomName,
      numberOfGuests: Number(room.adults) + Number(room.children),
      mealBasisDescription: room.mealBasis?.name,
      cancellations: room.cancellations,
      passengersDetails: room.passengersDetails,
      totalPrice: room.totalPrice,
      currencyShort: room.currencyShort
    })),
    currency: data?.rooms[0]?.currencyShort,
    priceBeforeMargin: data?.priceBeforeMargin,
    marginPercentage: data?.marginPercentage,
    originalPrice: data?.originalPrice,
    price: data?.totalPrice,
    totalPrice: data?.totalPrice,
    margin: data?.margin,
    totalCommission: data?.margin,
    ...(isCancelled && {
      status: 'CANCELLED'
    }),
    ...(isConfirmed && {
      status: 'CONFIRMED'
    })
  };
}

export const buildRoomPenalties = (room) => {
  const { penaltiesApplied } = room;
  const cancelPenalties = penaltiesApplied.filter(({ type }) => type === 'CANCEL');

  const penalty = cancelPenalties.reduce(
    (acc, { valueBeforeMargin, margin }) => ({
      value: acc.value + valueBeforeMargin ?? 0,
      margin: acc.margin + margin ?? 0
    }),
    { value: 0, margin: 0 }
  );

  return penalty;
};

export const buildHotelBookingPenalties = (hotelsData) =>
  hotelsData?.hotelBooking?.rooms.reduce(
    (acc, room) => {
      const penalty = buildRoomPenalties(room);

      return {
        margin: acc.margin + penalty.margin ?? 0,
        value: acc.value + penalty.value ?? 0
      };
    },
    { value: 0, margin: 0 }
  );

export const isHotelBookingCanceled = (hotelsData) =>
  hotelsData?.hotelBooking?.rooms?.every(({ status }) => status === 'CANCELED') ?? false;
